import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Field,
  FieldDescription,
  FieldGroup,
  FieldLabel,
  Input
} from '@repo/ui'
import { useForm } from '@tanstack/react-form'
import { Link, createFileRoute } from '@tanstack/react-router'
import * as Sentry from '@sentry/react'
import { useState } from 'react'
import { z } from 'zod'
import { apiPasswordReset } from '@/api/user/passwordReset'
import { zodFieldValidator } from '@/common/parseDataType'

const emailFieldSchema = z.email('Please enter a valid email address')

export const Route = createFileRoute('/user/password-reset')({
  head: () => ({ meta: [{ title: 'Reset Password - Snowvault' }] }),
  component: PasswordReset
})

function PasswordReset() {
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | undefined>(undefined)

  const form = useForm({
    defaultValues: {
      email: ''
    },
    onSubmit: async ({ value }) => {
      setError(undefined)
      try {
        await apiPasswordReset(value.email)
        setSuccess(true)
      } catch (err) {
        Sentry.captureException(err)
        console.error('Password reset error:', err)
        setError(err instanceof Error ? err.message : 'Failed to send reset link. Please try again.')
      }
    }
  })

  return (
    <div className="flex min-h-svh w-full items-center justify-center p-6 md:p-10">
      <div className="w-full max-w-sm">
        <Card>
          <CardHeader>
            <CardTitle>Reset your password</CardTitle>
            <CardDescription>
              {success
                ? 'Check your email for a password reset link.'
                : 'Enter your email and we will send you a reset link.'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {success ? (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  If an account exists for that email, a reset link is on its way.
                </p>
                <Field>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setSuccess(false)
                      form.reset()
                    }}
                  >
                    Send again
                  </Button>
                </Field>
                <FieldDescription className="text-center">
                  <Link to="/login" className="underline underline-offset-4 hover:no-underline">
                    Back to sign in
                  </Link>
                </FieldDescription>
              </div>
            ) : (
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  form.handleSubmit()
                }}
              >
                <FieldGroup>
                  <form.Field
                    name="email"
                    validators={{ onChange: zodFieldValidator(emailFieldSchema) }}
                  >
                    {(field) => (
                      <Field>
                        <FieldLabel htmlFor={field.name}>Email</FieldLabel>
                        <Input
                          id={field.name}
                          name={field.name}
                          type="email"
                          placeholder="Enter your email"
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                        />
                        {field.state.meta.errors.length > 0 && (
                          <FieldDescription className="text-destructive">
                            {String(field.state.meta.errors[0])}
                          </FieldDescription>
                        )}
                      </Field>
                    )}
                  </form.Field>
                </FieldGroup>
                <div className="pt-4">
                  <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                    {([canSubmit, isSubmitting]) => (
                      <Field>
                        <Button type="submit" disabled={!canSubmit || isSubmitting}>
                          {isSubmitting ? 'Sending...' : 'Send reset link'}
                        </Button>
                        <FieldDescription className="text-center">
                          <Link to="/login" className="underline underline-offset-4 hover:no-underline">
                            Back to sign in
                          </Link>
                        </FieldDescription>
                      </Field>
                    )}
                  </form.Subscribe>
                </div>
                {error && <p className="pt-2 text-sm text-destructive">{error}</p>}
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
